import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  AttachmentsField,
  DescriptionFieldForm,
  ExplanationTooltip,
  FlexColumn,
  FlexSpace,
  SelectField
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useOrgTypes from '@ubeya/shared/hooks/account/useOrgTypes';
import useCanAccessOrgDashboard from '@ubeya/shared/hooks/account/useCanAccessOrgDashboard';
import { ORG_TYPE_KINDS } from '@ubeya/shared/constants/orgTypes';
import { LOCATION_ATTACHMENTS_EXTENSIONS } from '@ubeya/shared/constants';
import LocationModalFields from './LocationModalFields';
import { MAX_LOCATION_ATTACHMENTS, LOCATION_INSTRUCTIONS_MAX_LENGTH } from 'settings/constants';

const Container = styled(FlexSpace)`
  width: 100%;
`;

const LocationFieldsContainer = styled(FlexColumn)`
  gap: 20px;
`;

const SubLocationFields = ({ selectedSubLocation, parentLocation }) => {
  const { t } = useContext(ConfigContext);
  const { config } = useAccount();
  const { orgTypes: areaTypes } = useOrgTypes(ORG_TYPE_KINDS.AREA_TYPES);
  const canAccessOrg = useCanAccessOrgDashboard();
  const { branchId } = useParams();
  const { mappedBranches } = useBranches();
  const branchTypeId = mappedBranches[Number(branchId)]?.orgBranchTypeId ?? null;

  const initialCenterLocation = {
    ...parentLocation,
    ...(selectedSubLocation?.lat ? { lat: selectedSubLocation?.lat, lng: selectedSubLocation?.lng } : {})
  };

  const areaTypeOptions = useMemo(() => {
    const selectedTypeId = selectedSubLocation?.orgAreaTypeId ?? null;
    const filtered = areaTypes.filter(
      (areaType) =>
        areaType.orgBranchTypeId == null ||
        branchTypeId == null ||
        areaType.orgBranchTypeId === branchTypeId ||
        areaType.id === selectedTypeId
    );
    return filtered.map(({ id, name }) => ({ value: id, label: name }));
  }, [areaTypes, branchTypeId, selectedSubLocation]);

  const showAreaTypeField = canAccessOrg && !!config?.organizationId && areaTypes.length > 0;

  return (
    <LocationFieldsContainer>
      <Container>
        <LocationModalFields
          selectedLocation={selectedSubLocation}
          initialCenterLocation={initialCenterLocation}
          optionalAddress
          editableAddress
          afterNameField={
            showAreaTypeField ? (
              <SelectField
                name="orgAreaTypeId"
                title={t('orgAreaType')}
                options={areaTypeOptions}
                isMulti={false}
                clearable
              />
            ) : null
          }
        />
      </Container>

      <DescriptionFieldForm
        name="instructions"
        label={t('instructions')}
        maxLength={LOCATION_INSTRUCTIONS_MAX_LENGTH}
        autoFocus={false}
      />

      <AttachmentsField
        title={<ExplanationTooltip title={t('attachFiles')} body={t('tooltipAttachFilesLocations')} />}
        dataName="attachments"
        fieldContext="subLocation"
        withAttachmentsActions={false}
        maxAttachments={MAX_LOCATION_ATTACHMENTS}
        allowedAttachmentsExtensions={LOCATION_ATTACHMENTS_EXTENSIONS}
      />
    </LocationFieldsContainer>
  );
};

export default SubLocationFields;
